import React, { useState } from 'react';
import { cn } from '@/lib/utils';

export interface StepItem {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string;
}

interface AccordionItemProps {
  item: StepItem;
  isActive: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  totalItems: number;
}

const AccordionStepItem: React.FC<AccordionItemProps> = ({ 
  item, 
  isActive, 
  onMouseEnter,
  onClick
}) => {
  return (
    <div
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "relative h-full rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ease-in-out",
        isActive ? "flex-[4]" : "flex-[0.5]",
        item.accentColor
      )}
    >
      {/* Step Number Badge */}
      <div className={cn(
        "absolute top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm transition-opacity duration-300",
        isActive ? "opacity-0" : "opacity-100"
      )}>
        {item.id}
      </div>

      {/* Vertical Title (shown when collapsed) */}
      <div className={cn(
        "absolute inset-0 flex items-center justify-center transition-opacity duration-300",
        isActive ? "opacity-0" : "opacity-100"
      )}>
        <span 
          className="text-white font-semibold text-sm whitespace-nowrap"
          style={{ 
            writingMode: 'vertical-rl', 
            textOrientation: 'mixed',
            transform: 'rotate(180deg)'
          }}
        >
          {item.title}
        </span>
      </div>

      {/* Expanded Content */}
      <div className={cn(
        "absolute inset-0 p-6 flex flex-col justify-center transition-opacity duration-300 delay-100",
        isActive ? "opacity-100" : "opacity-0"
      )}>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
            {item.id}
          </div>
          <div className="text-white/80">
            {item.icon}
          </div>
        </div>
        <h3 className="text-white font-bold text-lg mb-2 leading-tight">
          {item.title}
        </h3>
        <p className="text-white/80 text-sm leading-relaxed">
          {item.description}
        </p>
      </div>
    </div>
  );
};

interface InteractiveStepAccordionProps {
  steps: StepItem[];
  defaultActiveIndex?: number;
  className?: string;
}

export function InteractiveStepAccordion({ 
  steps, 
  defaultActiveIndex = 0,
  className 
}: InteractiveStepAccordionProps) {
  const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);

  return (
    <div className={cn("flex gap-2 h-80", className)}>
      {steps.map((step, index) => (
        <AccordionStepItem
          key={step.id}
          item={step}
          isActive={activeIndex === index}
          onMouseEnter={() => setActiveIndex(index)}
          onClick={() => setActiveIndex(index)}
          totalItems={steps.length}
        />
      ))}
    </div>
  );
}
